import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Alert, Box, Button, Collapse, Typography } from '@mui/material'
import { useId } from 'react'
import type { Notice, NoticeSeverity } from './types'

type NoticeBannerProps = { notice: Notice; expanded: boolean; onToggle: () => void }

// Local copy of MUI's visuallyHidden: @mui/utils is not a direct dependency.
const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const

const SEVERITY_PREFIX: Record<NoticeSeverity, string> = {
  error: 'Error: ',
  warning: 'Advertencia: ',
}

const NoticeBanner = ({ notice, expanded, onToggle }: NoticeBannerProps) => {
  const summaryId = useId()
  const detailId = useId()

  return (
    <Alert
      role="none"
      severity={notice.severity}
      sx={{
        py: 0,
        '& .MuiAlert-icon': { py: 0.75 },
        '& .MuiAlert-message': { flexGrow: 1, minWidth: 0, py: 0.75 },
      }}
      variant="standard"
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1 }}>
        <Typography component="span" id={summaryId} variant="body2">
          <Box component="span" sx={visuallyHiddenSx}>
            {SEVERITY_PREFIX[notice.severity]}
          </Box>
          {notice.title && <strong>{notice.title} </strong>}
          {notice.summary}
        </Typography>
        {notice.detail && (
          <Button
            aria-controls={expanded ? detailId : undefined}
            aria-describedby={summaryId}
            aria-expanded={expanded}
            color="inherit"
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            size="small"
            sx={{ py: 0, whiteSpace: 'nowrap' }}
            onClick={onToggle}
          >
            {expanded ? 'Ocultar detalle' : 'Ver detalle'}
          </Button>
        )}
      </Box>
      {notice.detail && (
        <Collapse unmountOnExit in={expanded}>
          <Box
            aria-label={notice.detailLabel}
            id={detailId}
            role="group"
            sx={{
              mt: 0.5,
              pr: 1,
              maxWidth: 720,
              maxHeight: 'min(240px, 40vh)',
              overflowY: 'auto',
              fontVariantNumeric: 'tabular-nums',
              typography: 'body2',
            }}
            tabIndex={0}
          >
            {notice.detail}
          </Box>
        </Collapse>
      )}
    </Alert>
  )
}

export default NoticeBanner
